<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { CART_STEPS } from '../../../enums';
import type { EquipmentRentalAgreementOption } from '../../../../EquipmentRental/types/equipmentRental.types';
import type { EquipmentRentalLineCheckoutPayload } from '../../../services/completeEquipmentRentalManualCheckout.service';
import { ADOPT_URL } from '@Constants';
import {
  resolveDefaultRentalAgreementId,
  shouldApplyDefaultRentalAgreement,
} from '../../../../EquipmentRental/helpers/equipmentRentalAgreementDefaults';
import { getTodayIsoDate, toIsoDateString } from '../../../../EquipmentRental/helpers/equipmentRentalDate.helpers';
import { getEquipmentRentalRentalAgreementsService } from '../../../../EquipmentRental/services';
import { EQUIPMENT_RENTAL_NO_AGREEMENT_ID } from '../../../../EquipmentRental/constants/equipmentRental.constants';
import { useCartSteps } from '../../../composables';
import {
  CompleteEquipmentRentalManualCheckoutService,
  DeleteCartItem,
  DeleteCartSessionService,
  SendCheckoutLinkService,
} from '../../../services';
import useCartStore from '../../../store';
import { useADialog, useAToast } from '@Atoms';
import OEquipmentRentalMobileCheckoutDialog from './OEquipmentRentalMobileCheckoutDialog.vue';

const emits = defineEmits<{ onStepUpdate: [step: CART_STEPS] }>();

const { cartData, cartId, loading: cartLoading, pendingOutcomeRoute, people } = storeToRefs(useCartStore());
const { outcomeCompletedCartReset, resetCart } = useCartStore();

const { activeDialog, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { notify, TOAST_TYPES } = useAToast();
const { previousStep } = useCartSteps();
const { t } = useI18n();
const router = useRouter();

const agreements = ref<EquipmentRentalAgreementOption[]>([]);
const agreementsLoading = ref(true);
const completeCheckoutLoading = ref(false);
const formTouched = ref(false);
const lineEdits = ref<
  Record<
    string,
    {
      rental_agreement_id: string;
      due_date: string | Date | undefined;
      rental_notes: string;
      rental_start_date: string | Date | undefined;
    }
  >
>({});
const mobileCheckoutModalVisible = ref(false);
const mobileLinkLoading = ref(false);

const equipmentLines = computed(() => {
  const raw = cartData.value?.equipment_lines;
  return Array.isArray(raw) ? raw : [];
});

const agreementDropdownOptions = computed((): EquipmentRentalAgreementOption[] => {
  const none: EquipmentRentalAgreementOption = {
    _id: EQUIPMENT_RENTAL_NO_AGREEMENT_ID,
    title: t('equipmentRental.startRental.step2.noAgreementTitle'),
    body: '',
    is_default: false,
  };
  return [...agreements.value, none];
});

const defaultAgreementId = computed(() => resolveDefaultRentalAgreementId(agreements.value));

/**
 * True when EVERY cart line was checked out as "No agreement required" (or the staff never touched
 * the picker and the shelter default itself is no-agreement). In that case neither mobile nor
 * manual signing makes sense — there's no contract to sign — so the footer collapses to a single
 * "Complete Checkout" that routes through the manual handler (which already creates rentals,
 * flips inventory, and redirects to the completion view). A single line with a real agreement is
 * enough to keep the Mobile + Manual pair visible.
 */
const allCartLinesAreNoAgreement = computed(() => {
  if (!equipmentLines.value.length) return false;
  return equipmentLines.value.every((line) => {
    const lineId = String(line.line_id ?? '');
    const edit = lineEdits.value[lineId];
    const agreementId = edit?.rental_agreement_id ?? defaultAgreementId.value;
    return !agreementId || agreementId === EQUIPMENT_RENTAL_NO_AGREEMENT_ID;
  });
});

const adopterAppLink = computed(() => `${ADOPT_URL}/mobile-checkout/${cartId.value}`);

watch([equipmentLines, defaultAgreementId], syncLineDefaults, { immediate: true });
watch(mobileCheckoutModalVisible, onMobileCheckoutModalVisibleChange);

onMounted(onMount);

function agreementMeta(id: string): EquipmentRentalAgreementOption | undefined {
  if (id === EQUIPMENT_RENTAL_NO_AGREEMENT_ID) return undefined;

  return agreements.value.find((agreement) => agreement._id === id);
}

function syncLineDefaults() {
  // Wait for agreements to load — seeding with the no-agreement fallback would lock every line into
  // it (shouldApplyDefaultRentalAgreement treats the sentinel as an explicit pick on the re-run).
  if (agreementsLoading.value) return;

  const defaultId = defaultAgreementId.value;
  for (const line of equipmentLines.value) {
    const lineId = String(line.line_id ?? '');
    if (!lineId) continue;

    const existing = lineEdits.value[lineId];
    if (!existing) {
      lineEdits.value[lineId] = {
        rental_agreement_id: defaultId,
        due_date: '',
        rental_notes: '',
        rental_start_date: getTodayIsoDate(),
      };

      continue;
    }

    if (shouldApplyDefaultRentalAgreement(existing.rental_agreement_id, defaultId)) {
      lineEdits.value[lineId] = {
        ...existing,
        rental_agreement_id: defaultId,
      };
    }
  }
}

function onMobileCheckoutModalVisibleChange(isOpen: boolean) {
  if (isOpen) return;

  mobileLinkLoading.value = false;
}

function onMount() {
  agreementsLoading.value = true;

  return getEquipmentRentalRentalAgreementsService()
    .then((list) => (agreements.value = list))
    .catch(() => (agreements.value = []))
    .finally(() => {
      agreementsLoading.value = false;
      syncLineDefaults();
    });
}

/** Per-line due-date validation: when the staff actually picked a due date, it must sit strictly
 *  after the start date. Leaving due_date empty is fine — rentals can be open-ended. Surfaces a
 *  red label + inline message below the affected line's due-date picker (see template) and gates
 *  validateForm() from below. Reactive so the message clears live when the staff fixes the date. */
function dueDateErrorForLine(lineId: string): string {
  const lineEdit = lineEdits.value[lineId];
  const due = lineEdit?.due_date;
  const start = lineEdit?.rental_start_date;
  if (!due || !(due instanceof Date)) return '';
  if (!start || !(start instanceof Date)) return '';
  if (due.getTime() > start.getTime()) return '';

  return t('equipmentRental.checkout.dueDateAfterStartError');
}

function validateForm(): boolean {
  formTouched.value = true;
  for (const line of equipmentLines.value) {
    const lineId = String(line.line_id ?? '');
    const lineEdit = lineEdits.value[lineId];
    if (!toIsoDateString(lineEdit?.rental_start_date)) {
      const TOAST_ERROR = {
        type: TOAST_TYPES.ERROR,
        title: t('common.error'),
        message: t('equipmentRental.startRental.step2.startDateRequired'),
      };
      notify(TOAST_ERROR);

      return false;
    }

    if (dueDateErrorForLine(lineId)) {
      // Inline label below the field already explains the rule — surface a toast too so staff who
      // scrolled past the offending line know why the checkout didn't fire.
      const TOAST_ERROR = {
        type: TOAST_TYPES.ERROR,
        title: t('common.error'),
        message: t('equipmentRental.checkout.dueDateAfterStartError'),
      };
      notify(TOAST_ERROR);

      return false;
    }
  }

  return true;
}

function buildEquipmentRentalLines(): EquipmentRentalLineCheckoutPayload[] {
  const lines: EquipmentRentalLineCheckoutPayload[] = [];
  for (const line of equipmentLines.value) {
    const lineId = String(line.line_id ?? '');
    const lineEdit = lineEdits.value[lineId];
    const agreementId = lineEdit?.rental_agreement_id ?? EQUIPMENT_RENTAL_NO_AGREEMENT_ID;
    const meta = agreementMeta(agreementId);
    lines.push({
      line_id: lineId,
      // NOTE: the synthetic "no agreement" token is mapped to the sentinel the backend expects.
      rental_agreement_id:
        agreementId === EQUIPMENT_RENTAL_NO_AGREEMENT_ID ? '__equipment_rental_no_agreement__' : agreementId,
      due_date: toIsoDateString(lineEdit?.due_date),
      rental_notes: typeof lineEdit?.rental_notes === 'string' ? lineEdit.rental_notes.trim() : '',
      rental_start_date: toIsoDateString(lineEdit?.rental_start_date) || getTodayIsoDate(),
      agreement_title: meta?.title ?? 'Rental agreement',
      agreement_body: meta?.body ?? '',
    });
  }

  return lines;
}

function onContinueMobileCheckout() {
  if (!validateForm()) return;

  mobileCheckoutModalVisible.value = true;
}

function onEquipmentMobileCheckoutDialogSubmit(detail: {
  checkoutFlow: string;
  deviceCheckoutMethod: string;
  email: string;
  phone: string;
}) {
  mobileLinkLoading.value = true;

  const isAdopterFlow = detail.checkoutFlow === 'adopter';
  const isShelterLinkFlow = detail.checkoutFlow === 'shelter' && detail.deviceCheckoutMethod === 'link';
  const email = isAdopterFlow || isShelterLinkFlow ? detail.email : '';
  const phone = isAdopterFlow ? detail.phone : '';

  const rawCartId = cartData.value?.cart_id?.$oid || cartData.value?.cart_id;
  const PAYLOAD: Record<string, unknown> = {
    _id: rawCartId,
    cart_id: rawCartId,
    // Opt out of resetCartBeforeMobileCheckout — without this it wipes cart.people from the session.
    cart_type: 'equipment_rental',
    checkoutFlow: detail.checkoutFlow,
    email,
    payment_type: null,
    phone,
    show_application: null,
    show_contract: null,
    status: 'pending',
    equipment_rental_lines: buildEquipmentRentalLines(),
  };

  const TOAST_ERROR = {
    message: 'errors.generics.unknownIssue.message',
    title: 'errors.generics.unknownIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  SendCheckoutLinkService(PAYLOAD as any)
    .then(() => {
      if (detail.deviceCheckoutMethod === 'newTab') window.open(adopterAppLink.value, '_blank');
      mobileCheckoutModalVisible.value = false;
      return router.push(pendingOutcomeRoute.value!).then(() => outcomeCompletedCartReset());
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (mobileLinkLoading.value = false));
}

function onCancelCart() {
  activeDialog.value = SYSTEM_MODALS.CART_EMPTY_DIALOG;
  dialogVisibility.value = true;
}

function onRemoveLine(index: number) {
  cartLoading.value = true;

  const TOAST_SUCCESS = {
    title: 'cart.toast.removeItem.success.title',
    type: TOAST_TYPES.INFO,
  };
  const TOAST_ERROR = {
    type: TOAST_TYPES.ERROR,
    title: 'cart.toast.removeItem.error.title',
    message: 'cart.toast.removeItem.error.message',
  };

  return DeleteCartItem(index)
    .then((response: { data?: unknown }) => {
      cartData.value = (response?.data ?? response) as typeof cartData.value;
      notify(TOAST_SUCCESS);
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (cartLoading.value = false));
}

function onManualEquipmentCheckout() {
  if (!validateForm()) return;

  const rawCartId = cartData.value?.cart_id?.$oid || cartData.value?.cart_id;
  if (!rawCartId) return;

  const TOAST_SUCCESS = {
    title: 'cart.toast.equipmentRentalCheckoutComplete.title',
    message: 'cart.toast.equipmentRentalCheckoutComplete.message',
    type: TOAST_TYPES.SUCCESS,
  };

  const cartIdString = String(rawCartId);
  completeCheckoutLoading.value = true;

  return CompleteEquipmentRentalManualCheckoutService({
    cart_id: cartIdString,
    equipment_rental_lines: buildEquipmentRentalLines(),
  })
    .then(() => DeleteCartSessionService(false))
    .then(() => {
      resetCart();
      notify(TOAST_SUCCESS);
      return router.push({ name: 'equipment-rental-payment-completed', params: { id: cartIdString } });
    })
    .catch((error) => {
      const apiMessage = (error?.response?.data?.error ?? error?.response?.data?.message ?? '').toString().trim();
      const TOAST_ERROR = {
        type: TOAST_TYPES.ERROR,
        title: t('common.error'),
        message: apiMessage || t('errors.generics.unknownIssue.message'),
      };
      notify(TOAST_ERROR);
    })
    .finally(() => (completeCheckoutLoading.value = false));
}
</script>

<template>
  <div class="af-cart-grid justify-center">
    <div class="w-full">
      <div class="flex w-full flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <p
          v-if="agreementsLoading"
          class="text-sm text-slate-500"
        >
          {{ $t('common.loading') }}…
        </p>

        <div class="surface-border border-round surface-ground flex w-full flex-col gap-4 border-2 border-dashed p-4">
          <div
            v-for="(line, index) in equipmentLines"
            :key="String(line.line_id ?? index)"
            :class="['flex flex-col gap-4', index > 0 ? 'border-t border-gray-200 pt-4' : '']"
          >
            <div class="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div>
                <div class="font-semibold text-slate-900">
                  {{ line.equipment_name }}
                </div>

                <div class="text-xs text-slate-500">
                  {{ $t('equipmentRental.inventory.columns.equipment_label') }}: {{ line.equipment_label || line.equipment_id }}
                </div>
                <div class="text-xs text-slate-500">
                  {{ $t('equipmentRental.inventory.columns.serial_number') }}: {{ (line as any).serial_number || '—' }}
                </div>
              </div>

              <Button
                v-if="equipmentLines.length > 1"
                text
                class="af-button !text-xs"
                icon="pi pi-trash"
                severity="danger"
                :label="$t('common.remove')"
                @click="onRemoveLine(index)"
              />
            </div>

            <div class="flex flex-col gap-4">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-start">
                <div class="flex flex-col gap-2">
                  <AFieldLabel :label="$t('equipmentRental.checkout.agreementLabel')" />
                  <AFieldDropdown
                    v-model="lineEdits[String(line.line_id)]!.rental_agreement_id"
                    class="w-full"
                    optionLabel="title"
                    optionValue="_id"
                    :options="agreementDropdownOptions"
                  />
                </div>

                <!-- NOTE: placeholder cell so start/due land on row 2 (kept off the layout on mobile). -->
                <div class="hidden sm:block"></div>

                <div class="flex flex-col gap-2">
                  <AFieldLabel
                    required
                    :label="$t('equipmentRental.checkout.startDateLabel')"
                    :error="formTouched && !toIsoDateString(lineEdits[String(line.line_id)]?.rental_start_date)"
                  />
                  <MFieldDatePicker
                    v-model="lineEdits[String(line.line_id)]!.rental_start_date"
                    class="w-full"
                    :id="`er-start-${String(line.line_id)}`"
                  />
                </div>

                <div class="flex flex-col gap-2">
                  <AFieldLabel
                    :label="$t('equipmentRental.checkout.dueDateLabel')"
                    :error="!!dueDateErrorForLine(String(line.line_id))"
                  />
                  <MFieldDatePicker
                    v-model="lineEdits[String(line.line_id)]!.due_date"
                    noModelOnStart
                    class="w-full"
                    :pt="
                      dueDateErrorForLine(String(line.line_id))
                        ? { input: { class: '!border-red-500 focus:!border-red-500 focus:!ring-red-500' } }
                        : undefined
                    "
                    :id="`er-due-${String(line.line_id)}`"
                  />
                  <p
                    v-if="dueDateErrorForLine(String(line.line_id))"
                    class="text-xs text-red-500"
                  >
                    {{ dueDateErrorForLine(String(line.line_id)) }}
                  </p>
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <AFieldLabel :label="$t('equipmentRental.checkout.notesLabel')" />
                <AFieldTextArea
                  v-model="lineEdits[String(line.line_id)]!.rental_notes"
                  autoResize
                  rows="2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <aside class="w-full rounded-lg border border-gray-200 bg-white p-6 shadow-sm lg:sticky lg:top-4 lg:self-start">
      <header class="mb-4 flex items-center justify-between gap-2">
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('equipmentRental.checkout.summaryHeading') }}
        </h3>

        <Button
          outlined
          class="af-button !text-xs"
          icon="pi pi-trash"
          severity="danger"
          :label="$t('equipmentRental.checkout.cancelCheckout')"
          @click="onCancelCart"
        />
      </header>

      <ul class="mb-4 flex flex-col gap-2 text-sm">
        <li
          v-for="(line, index) in equipmentLines"
          :key="String(line.line_id ?? index)"
          class="rounded-md border border-gray-100 bg-slate-50 px-3 py-2"
        >
          <p class="font-medium text-gray-900">
            {{ line.equipment_name || '—' }}
          </p>

          <p
            v-if="line.equipment_id"
            class="text-xs text-gray-500"
          >
            {{ $t('equipmentRental.inventory.columns.equipment_label') }}: {{ line.equipment_label || line.equipment_id }}
          </p>
          <p
            v-if="line.equipment_id"
            class="text-xs text-gray-500"
          >
            {{ $t('equipmentRental.inventory.columns.serial_number') }}: {{ (line as any).serial_number || '—' }}
          </p>
        </li>
      </ul>

      <Divider class="my-3" />

      <div class="flex flex-col gap-2">
        <!--
          Every line is "No agreement required" → nothing to sign, no reason to round-trip the
          adopter device. Collapse the Mobile + Manual pair into a single Complete Checkout that
          routes through the same manual backend (creates the rental docs, flips inventory, lands
          on the completion view). A single line with a real agreement is enough to flip back to
          the full pair below.
        -->
        <Button
          v-if="allCartLinesAreNoAgreement"
          class="af-button af-button-success w-full"
          icon="pi pi-check"
          :loading="completeCheckoutLoading"
          :label="$t('cart.label.completeRentalCheckout')"
          @click="onManualEquipmentCheckout"
        />

        <template v-else>
          <Button
            class="af-button af-button-success w-full"
            icon="pi pi-mobile"
            :label="$t('cart.label.continueMobileCheckout')"
            @click="onContinueMobileCheckout"
          />

          <Button
            class="af-button af-button-success w-full"
            icon="pi pi-user"
            :loading="completeCheckoutLoading"
            :label="$t('cart.label.continueManualCheckout')"
            @click="onManualEquipmentCheckout"
          />
        </template>

        <Button
          v-if="previousStep"
          outlined
          class="af-button af-button-success-outlined w-full"
          icon="pi pi-arrow-left"
          :label="$t('common.back')"
          @click="emits('onStepUpdate', previousStep)"
        />
      </div>
    </aside>

    <OEquipmentRentalMobileCheckoutDialog
      v-model:visible="mobileCheckoutModalVisible"
      :people
      :submitLoading="mobileLinkLoading"
      @onSubmit="onEquipmentMobileCheckoutDialogSubmit"
    />
  </div>
</template>
